// Şube oluştur/düzenle sayfası (MOD-11 / K-2) — /new + /:id paylaşımlı.
//
// Save butonu başlıkta (form={BRANCH_FORM_ID}). Düzenlemede branch yüklenene
// dek form seed edilmez (RHF defaultValues yalnız mount'ta uygulanır → key ile
// remount). Güncelleme rowVersion ile iyimser kilit taşır (409 → concurrency).

import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Skeleton, Space } from 'antd';
import { ArrowLeft, Save } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { useCan } from '@shared/rbac/useCan';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { ROUTES } from '@shared/constants/routes';
import { branchesApi, citiesApi } from '@api/branches';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { BranchForm, BRANCH_FORM_ID } from '../components/BranchForm';
import type { BranchInput } from '../types';

export function BranchEditPage() {
  const { t } = useTranslation(['branches', 'common']);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const canManage = useCan(PERMISSIONS.BRANCH_MANAGE);

  const { data: branch, isLoading: branchLoading } = useQuery({
    queryKey: ['branch', id],
    queryFn: () => branchesApi.get(id ?? ''),
    enabled: isEdit,
  });

  const { data: cities = [], isLoading: citiesLoading } = useQuery({
    queryKey: ['cities'],
    queryFn: () => citiesApi.list(),
  });

  const backToList = () => {
    void navigate(ROUTES.BRANCHES);
  };

  const createMutation = useMutation({
    mutationFn: (input: BranchInput) => branchesApi.create(input),
    onSuccess: async () => {
      toast.success(t('toast.created'));
      await qc.invalidateQueries({ queryKey: ['branches'] });
      backToList();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: (input: BranchInput) =>
      branchesApi.update(id ?? '', input, branch?.rowVersion ?? null),
    onSuccess: async () => {
      toast.success(t('toast.updated'));
      await qc.invalidateQueries({ queryKey: ['branches'] });
      await qc.invalidateQueries({ queryKey: ['branch', id] });
      backToList();
    },
    onError: (err) => toast.error(resolveApiErrorMessage(err)),
  });

  const handleSubmit = (input: BranchInput) =>
    isEdit ? updateMutation.mutate(input) : createMutation.mutate(input);

  const saving = createMutation.isPending || updateMutation.isPending;
  const loading = isEdit && branchLoading;

  return (
    <div data-testid="branch-edit-page">
      <PageHeader
        title={isEdit ? t('edit.title') : t('new')}
        description={isEdit ? t('edit.description') : t('create.description')}
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: t('title'), to: ROUTES.BRANCHES },
          { label: isEdit ? t('edit.title') : t('new') },
        ]}
        actions={
          <Space>
            <Button icon={<ArrowLeft size={14} />} onClick={backToList}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              type="primary"
              icon={<Save size={14} />}
              htmlType="submit"
              form={BRANCH_FORM_ID}
              loading={saving}
              disabled={loading || !canManage}
              data-testid="branch-save"
            >
              {t('common:actions.save')}
            </Button>
          </Space>
        }
      />

      <Card>
        {loading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : (
          <BranchForm
            key={branch?.id ?? 'new'}
            branch={branch}
            cities={cities}
            citiesLoading={citiesLoading}
            onSubmit={handleSubmit}
          />
        )}
      </Card>
    </div>
  );
}
